/**
 * What a face needs from a race, and what it needs from a kingdom.
 *
 * This file is a *contract*, not a roster. It defines the two records a leader
 * portrait is drawn from and contains no races and no houses: those are data,
 * they belong in `src/data/`, and they are somebody else's to write. What is
 * here is the shape they have to fill in, and the reasons each field exists —
 * every one of which was paid for by a render that went wrong.
 *
 * ── Why it is two records and not one ───────────────────────────────────────
 *
 * The hardest thing being asked for is that two goblin kingdoms not look like
 * each other. That is not a colour problem and it cannot be solved by giving
 * one of them a different palette; it is decided entirely by whether race and
 * culture are separate layers.
 *
 *   RaceFace   the body. Skull, eyes, ears, teeth, hide, and what the species
 *              adds to its own outline. Every kingdom of that people shares it,
 *              exactly, and none of them may override it
 *   HouseLook  the kingdom, and the leader who is its face. What is worn on the
 *              head, how the hair is cut, what the metalwork is made of, what
 *              sits on the shoulders, how the head is held
 *
 * If culture leaks into `RaceFace` — if the goblins' race entry says "wears
 * scrap iron" — then the second goblin kingdom has nowhere left to differ and
 * the only lever remaining is the accent colour. That is the failure being
 * warned about, and it happens at the data-design stage, not at the drawing
 * stage. Nothing I can do in this file recovers from it.
 *
 * ── The acceptance rule ─────────────────────────────────────────────────────
 *
 * Two houses of the same race must differ in at least three of `crown`,
 * `grooming`, `material`, `mantle` and `bearing`. One is a recolour. Two is a
 * variant. Three is a different person, and `housesDiffer` below counts them so
 * the rule can be checked rather than remembered.
 *
 * ── What not to send ────────────────────────────────────────────────────────
 *
 * Not a single colour. A colour is the last tenth of a face and the first nine
 * tenths are structure — the standing complaint about the old troop cards was
 * that an orc and a militiaman were the same body twice, and they were, because
 * the only thing that differed was the fill.
 *
 * Not a texture. Scales, warts and fur are drawn as a handful of marks, never as
 * a pattern; a pattern at 46px is noise, and 46px is where the selection screen
 * thumbnails live.
 *
 * Not an adjective. "Proud", "ancient" and "cruel" cannot be drawn. A brow that
 * overhangs the eye, a face four units too long, and a mouth whose corners fall
 * can all be drawn, and between them they say the same thing.
 *
 * ── Traps, all of which have already caught this repo once ──────────────────
 *
 *   two points above the hairline   read as horns, whatever they were meant to
 *                                   be. This cost the Huntmaster his hood and
 *                                   then, when the hood was gone, his fringe
 *   a closed loop around the mouth  reads as a muzzle. Folds that hook back in
 *                                   under the mouth corners will do it
 *   a dark mass enclosing the head  reads as a hood, even when it is hair
 *   a nose drawn with mass          reads as a snout. A nose can only be implied
 *   an oval with features on it     reads as a doll. A face needs a *corner* at
 *                                   the cheekbone and another at the jaw
 *   everything at one scale         reads as one thing. Five heroes cropped
 *                                   within 8% of each other were five faces
 *                                   nobody could tell apart at 30px, and
 *                                   spreading the crop fixed more than any
 *                                   amount of anatomy did
 */

/* ── Layer one: the body ─────────────────────────────────────────────────── */

/**
 * A people's face, as multipliers on a human one.
 *
 * Everything geometric here is a ratio where 1 is human, so a race can be
 * specified as "jaw 1.5, chin 0.6, cranium 0.8" without anyone outside this
 * directory needing to know the drawing frame, the landmark names or which way
 * the light comes from. That is deliberate: the numbers in `portraits.ts` have
 * been re-tuned four times and will be again, and a race defined against them
 * directly would break every time.
 *
 * The useful range for most of these is 0.6 to 1.6. Past that the head stops
 * being a head — which is a legitimate thing to want, but say so with `crest`,
 * `hide` and `muzzle` rather than by setting `jaw` to 3.
 */
export interface RaceFace {
  id: string;

  // ── The skull ────────────────────────────────────────────────────────────
  //
  // Six landmarks, and between them they are the difference between a dwarf and
  // an elf without either of them being tinted. Note that `cranium` and `jaw`
  // are the pair that does the most work and they are worth setting in
  // opposition: a big vault over a small jaw and a small vault over a heavy one
  // are two silhouettes nobody will confuse, and either can be a goblin.

  /** Height of the vault above the eyeline. Big brain, or none. */
  cranium: number;
  /** Half-width at the temple. */
  temple: number;
  /** Half-width at the cheekbone. */
  cheek: number;
  /** Where the cheekbone sits between eye and jaw. 0 high and gaunt, 1 low and soft. */
  cheekHeight: number;
  /** Half-width at the jaw corner. Above ~1.3 it must exceed `temple` or the head reads as a pear. */
  jaw: number;
  /** Width across the chin point. Set this equal to `jaw` and the head is a brick — the Warlord's was. */
  chin: number;
  /** Crown to chin. */
  faceLength: number;
  /** How far the brow bone overhangs the eye. 0 none, 1 human, 2 sinks the eyes into shadow. */
  browRidge: number;
  /** Forward projection of the midface. 0 flat, 1 a clear snout. Drawn as a second lobe off the skull, never by stretching it. */
  muzzle: number;

  // ── The eyes ─────────────────────────────────────────────────────────────
  //
  // At thumbnail size the whole eye is three pixels across, so `pupil`,
  // `sclera` and `glow` are worth more than every geometric field above them
  // put together: they are readable at any size and they say "not human"
  // instantly and for free. Use them.

  eyeSize: number;
  eyeSpacing: number;
  /** How far the outer corner rises above the inner. Negative droops, and droop reads as age or sorrow. */
  eyeSlant: number;
  /**
   * Upper lid coverage. 0 wide open, 1 hooded.
   *
   * This used to say a wide lid "reads as alarm on most faces". It does not,
   * and the wording cost somebody a wasted investigation: the Cog-Warrens'
   * Underwright is 0.04 on the largest eye in the roster and reads as *staring*
   * rather than frightened, because her brow sits level. **Alarm comes from a
   * raised brow, not from a wide lid.** If you want alarm, lift `browTilt`; if
   * you want intensity, open the lid and leave the brow where it is.
   */
  lid: number;
  pupil: 'round' | 'slit' | 'goat' | 'pinpoint' | 'none';
  /** Whether the white shows. `dark` is an all-black eye; `none` is a socket. */
  sclera: 'white' | 'dark' | 'none';
  /** 0 an ordinary eye, 1 the eye is the light source in the picture. */
  glow: number;
  /** The colours this people's eyes come in. One entry is fine; a leader picks from it. */
  iris: readonly number[];

  // ── The ears ─────────────────────────────────────────────────────────────

  /**
   * The cheapest race tell there is, and the only condition on it is that the
   * ear must break the outline rather than sit inside it. Elf ears drawn inside
   * the head's silhouette are a texture nobody sees; drawn past it they are the
   * whole identification.
   */
  //
  // Vertical placement is the renderer's business and does not read
  // `cheekHeight`, so that field is safe at both ends. It was not always: every
  // ear once hung off the cheekbone, and `cheekHeight` of 0.22 puts an elf's
  // cheekbone above the eyeline, which grew a long swept ear out of the corner
  // of the eye. Two rules now, and between them nothing depends on the field —
  // an elf's ear hangs from the eyeline, a beast's `tufted` ear rides the
  // skull. The true low holder is the Pale Litany at 0.20, and it never showed
  // there only because they have no ears at all. Luck is not a fix.
  ear: 'human' | 'long' | 'swept' | 'lobed' | 'batwing' | 'round' | 'tufted' | 'fin' | 'cropped' | 'none';
  earLength: number;

  // ── The mouth ────────────────────────────────────────────────────────────

  mouthWidth: number;
  /** Lip fullness. 0 is no lip at all, which is correct for beaks, bone and chitin. */
  lip: number;
  tusk: 'none' | 'lower' | 'upper' | 'boar';
  tuskSize: number;

  // ── The surface ──────────────────────────────────────────────────────────

  /**
   * Three stops, not one: base, shade and deep. A face filled with a single
   * colour is a doll no matter how good the geometry under it is — this was the
   * finding that turned the hero portraits around, and the ramp is what the key
   * light lands on.
   *
   * Send a ramp whose base is either clearly lighter or clearly darker than the
   * ash the portrait sits in. Every faction cloth in this game was picked to
   * *hide* on the map, and every one of them had to be lifted for the cards
   * because figure and ground came out the same value.
   */
  skin: readonly [base: number, shade: number, deep: number];
  /** Drawn as a few marks over the ramp. Never as a repeating pattern. */
  hide: 'smooth' | 'scaled' | 'warty' | 'furred' | 'chitin' | 'bare' | 'wrapped';
  /** Where hair can grow at all. `none` forces the silhouette to be made by `crest` or by the house. */
  hairGrowth: 'full' | 'crest' | 'patchy' | 'none';
  hair: readonly number[];

  // ── The envelope ─────────────────────────────────────────────────────────

  /**
   * What the species adds to its own outline, before anyone puts anything on
   * its head.
   *
   * This is the single most valuable field in the record, because at 30px the
   * outline is all that survives. It is also the most dangerous: anything sharp
   * rising above the hairline reads as horns whether it was meant to or not, so
   * `none` means *nothing pointed up there at all*, and a crest that is wanted
   * should be committed to and drawn large. Half a horn is a mistake; a whole
   * one is a character.
   */
  crest: 'none' | 'horn-pair' | 'horn-swept' | 'horn-ram' | 'antler' | 'frill' | 'quill' | 'bone-plate';
  crestSize: number;

  // ── Framing ──────────────────────────────────────────────────────────────

  /**
   * How much of the portrait this people's head takes.
   *
   * Not cosmetic. Spreading this across the five heroes did more for telling
   * them apart at thumbnail size than every change to their faces combined, and
   * a race roster that leaves it at 1 throughout has thrown away its best lever
   * before drawing anything. An ogre-kin leader should crowd the arch; a sprite
   * should sit small in it with air above the head.
   */
  frameScale: number;
  neck: number;
  shoulder: number;
}

/**
 * The reference. Every multiplier above is against this, and it is here so a
 * race entry can be written as a spread with only the fields that differ —
 * which is also the honest way to read one: the fields that are *not* listed
 * are the ones where this people is human.
 */
export const HUMAN_BASELINE: RaceFace = {
  id: 'human',
  cranium: 1,
  temple: 1,
  cheek: 1,
  cheekHeight: 0.5,
  jaw: 1,
  chin: 1,
  faceLength: 1,
  browRidge: 1,
  muzzle: 0,
  eyeSize: 1,
  eyeSpacing: 1,
  eyeSlant: 1,
  lid: 0.22,
  pupil: 'round',
  sclera: 'white',
  glow: 0,
  iris: [0x5f4a32, 0x4a5a48, 0x3d4a5c],
  ear: 'human',
  earLength: 1,
  mouthWidth: 1,
  lip: 1,
  tusk: 'none',
  tuskSize: 0,
  skin: [0xcaa98a, 0x9b7c60, 0x74533d],
  hide: 'smooth',
  hairGrowth: 'full',
  hair: [0x2b241d, 0x50412c, 0x8b7d64, 0x6a5238],
  crest: 'none',
  crestSize: 0,
  frameScale: 1,
  neck: 1,
  shoulder: 1,
};

/* ── Layer two: the kingdom, and its leader ──────────────────────────────── */

/**
 * A house's look. This is where two kingdoms of one people become two peoples.
 *
 * None of these fields touches the skull. That is the point: the goblins of one
 * kingdom and the goblins of the next have identical bone and are told apart
 * entirely by what has been done to it — one shaved and branded under a scrap
 * iron half-helm, the other in a lacquered mask with a plaited topknot. Same
 * face, no shared read.
 */
export interface HouseLook {
  id: string;
  /** The `RaceFace.id` this house is drawn on. Several houses may share one. */
  race: string;
  /** The name under the portrait on the selection screen. */
  leader: string;

  /**
   * The one saturated colour. It lights the niche behind the figure and appears
   * on at most three things in front of it. The world is ash and bone; a
   * portrait that glows will look wrong next to the map, and the map is what it
   * sits on.
   */
  accent: number;
  accentDark: number;

  /** What is worn on the head — the strongest identifier after the face itself. */
  //
  // `hood` was here and is deliberately gone. It is the one shape this repo has
  // had to remove twice — from the Huntmaster, for reading as a cowl at
  // thumbnail size, and then again when his hair rebuilt one by accident — and
  // `coif` and `veil` between them cover everything it was for. A vocabulary
  // should not carry an entry whose whole behaviour is the failure mode its
  // owner keeps fixing. Removing it from the type turns "no house uses this"
  // from a test assertion into a compile error, which is the better failure.
  crown:
    | 'bare'
    | 'circlet'
    | 'crown-heavy'
    | 'mitre'
    | 'coif'
    | 'veil'
    | 'mask'
    | 'helm-open'
    | 'helm-closed'
    | 'helm-horned'
    | 'wreath'
    | 'antler-rack';
  grooming: 'cropped' | 'long' | 'braided' | 'topknot' | 'shaved' | 'shaved-marked' | 'wild' | 'bald' | 'tonsure';
  beard: 'none' | 'stubble' | 'short' | 'forked' | 'long' | 'moustache' | 'plaited';

  /**
   * The material language of the house's metalwork and ornament, and the field
   * most often left out. It is what makes a set of unrelated shapes feel like
   * one people: scrap iron and rag is a different civilisation from lacquer and
   * glass even when the two are worn by identical heads.
   */
  material: 'iron' | 'steel' | 'gold' | 'bronze' | 'bone' | 'coral' | 'lacquer' | 'rag' | 'glass' | 'wood' | 'stone';

  /** The shoulders, which are two fifths of the picture's area and usually an afterthought. */
  mantle: 'gorget' | 'pauldrons' | 'pelt' | 'robe' | 'chasuble' | 'scale' | 'harness' | 'bare';

  /**
   * Attitude, and it is per-leader rather than per-race.
   *
   * The reference these are being drawn against is memorable because each god
   * has a bearing you could pick out at thumbnail size — one is looking past
   * you, one is looking down, one is not looking at all. Five characters facing
   * front with level heads and neutral mouths will read as one character five
   * times however different their skulls are.
   */
  bearing: {
    /** Degrees. Positive tips the chin up and the head back. About ±8 is the useful range. */
    tilt: number;
    gaze: 'open' | 'narrow' | 'shut' | 'blind' | 'aside';
    /** Corners of the mouth. Positive up, negative down. */
    mouth: number;
  };

  /** One asymmetric thing that is this leader's alone. A named character needs it; a race must not have it. */
  mark: 'none' | 'scar' | 'blind-eye' | 'brand' | 'broken-tusk' | 'warpaint' | 'tattoo' | 'burn';
  /** 0 young, 1 old. Drives folds, jowls and forehead lines, which are only drawn when earned. */
  age: number;
}

/**
 * How many of the five identity levers two houses actually differ in.
 *
 * The rule is three. It is stated as a function rather than as a comment so the
 * race roster can assert it: two kingdoms of one people scoring below three
 * will read as the same kingdom in two colours at the size the selection screen
 * draws them, and no amount of work on the face underneath will rescue that,
 * because the face underneath is by definition identical.
 */
export function housesDiffer(a: HouseLook, b: HouseLook): number {
  let n = 0;
  if (a.crown !== b.crown) n++;
  if (a.grooming !== b.grooming) n++;
  if (a.material !== b.material) n++;
  if (a.mantle !== b.mantle) n++;
  if (a.bearing.gaze !== b.bearing.gaze || Math.abs(a.bearing.tilt - b.bearing.tilt) >= 5) n++;
  return n;
}
